import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
  timeout: 60000,  // v48: parche defensivo para dashboards pesados (backend ya cachea 5m).
});

// Auto-refresh on 401
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry && !original.url.includes("/auth/")) {
      original._retry = true;
      try {
        if (!refreshing) refreshing = api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(original);
      } catch {
        refreshing = null;
        return Promise.reject(err);
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) return err?.message || "Ocurrió un error inesperado";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(d);
}

// ---- Auth ----
export const authApi = {
  login:   (email, password) => api.post("/auth/login", { email, password }).then((r) => r.data),
  logout:  () => api.post("/auth/logout").then((r) => r.data),
  me:      () => api.get("/auth/me").then((r) => r.data),
};

// ---- Data ----
export const dataApi = {
  users:         () => api.get("/users").then((r) => r.data),
  createUser:    (body) => api.post("/users", body).then((r) => r.data),
  updateUser:    (id, body) => api.patch(`/users/${id}`, body).then((r) => r.data),
  toggleUser:    (id) => api.patch(`/users/${id}/status`).then((r) => r.data),
  verifyUser:    (id) => api.post(`/users/${id}/verify`).then((r) => r.data),
  resetPassword: (id) => api.post(`/users/${id}/reset-password`, {}).then((r) => r.data),
  unlockUser:    (id) => api.post(`/users/${id}/unlock`).then((r) => r.data),
  deleteUser:    (id) => api.delete(`/users/${id}`).then((r) => r.data),
  drivers:       (available) => api.get("/drivers", { params: available ? { available: 1 } : {} }).then((r) => r.data),
  vehicles:      () => api.get("/vehicles").then((r) => r.data),
  createVehicle: (body) => api.post("/vehicles", body).then((r) => r.data),
  kpis:          () => api.get("/kpis").then((r) => r.data),
  analyticsDashboard: (range) => api.get("/analytics/dashboard", { params: range ? { range } : {} }).then((r) => r.data),
  analyticsExport: (range = "30d") => api.get("/analytics/export", { params: { range }, responseType: "blob" }).then((r) => r.data),
  // Zones
  zones:         () => api.get("/zones").then((r) => r.data),
  createZone:    (body) => api.post("/zones", body).then((r) => r.data),
  updateZone:    (id, body) => api.patch(`/zones/${id}`, body).then((r) => r.data),
  deleteZone:    (id) => api.delete(`/zones/${id}`).then((r) => r.data),
  updateZonePolygon: (id, polygon) => api.patch(`/zones/${id}/polygon`, { polygon }).then((r) => r.data),
  // Tariffs
  tariffs:       () => api.get("/tariffs").then((r) => r.data),
  createTariff:  (body) => api.post("/tariffs", body).then((r) => r.data),
  updateTariff:  (id, body) => api.patch(`/tariffs/${id}`, body).then((r) => r.data),
  deleteTariff:  (id) => api.delete(`/tariffs/${id}`).then((r) => r.data),
  // Rides
  rides:         (status) => api.get("/rides", { params: status ? { status } : {} }).then((r) => r.data),
  createRide:    (body) => api.post("/rides", body).then((r) => r.data),
  assignRide:    (id, driver_id) => api.post(`/rides/${id}/assign`, { driver_id }).then((r) => r.data),
  startRide:     (id) => api.post(`/rides/${id}/start`).then((r) => r.data),
  completeRide:  (id, body) => api.post(`/rides/${id}/complete`, body || {}).then((r) => r.data),
  cancelRide:    (id) => api.post(`/rides/${id}/cancel`).then((r) => r.data),
  botConvos:     () => api.get("/bot/conversations").then((r) => r.data),
  // Bot Logs (diagnóstico 2chat)
  botLogs:       (params) => api.get("/bot/logs", { params }).then((r) => r.data),
  botLogsStats:  () => api.get("/bot/logs/stats").then((r) => r.data),
  // Admin bulk operations
  bulkImportDrivers: (body) => api.post("/admin/drivers/bulk-import", body).then((r) => r.data),
  importPassengers: (file, defaultCountryCode = "51") => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/admin/passengers/import-phones", fd, {
      params: { default_country_code: defaultCountryCode },
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  broadcastWhatsApp: (body) => api.post("/admin/whatsapp/broadcast", body).then((r) => r.data),
  onboardingWelcome: (body) => api.post("/admin/drivers/onboarding-welcome", body).then((r) => r.data),
  driversEngagement: () => api.get("/admin/drivers/engagement").then((r) => r.data),
  // ─── Promotions ────────────────────────────────────────────────────
  listPromotions: () => api.get("/admin/promotions").then((r) => r.data),
  createPromotion: (body) => api.post("/admin/promotions", body).then((r) => r.data),
  updatePromotion: (id, body) => api.patch(`/admin/promotions/${id}`, body).then((r) => r.data),
  listPromoClaims: (id) => api.get(`/admin/promotions/${id}/claims`).then((r) => r.data),
  // ─── Broadcast push notifications ─────────────────────────────────
  broadcastPush: (body) => api.post("/admin/notifications/broadcast", body).then((r) => r.data),
  listBroadcasts: () => api.get("/admin/notifications/broadcasts").then((r) => r.data),
  // ─── Broadcast WhatsApp masivo vía 2chat ──────────────────────────
  broadcastWhatsApp2chat: (body) => api.post("/admin/notifications/broadcast-whatsapp", body).then((r) => r.data),
  whatsappBroadcastStatus: (id) => api.get(`/admin/notifications/broadcast-whatsapp/${id}`).then((r) => r.data),
  // ─── POIs TAXIGO ──────────────────────────────────────────────────
  listPOIs: (params = {}) => api.get("/admin/pois", { params }).then((r) => r.data),
  createPOI: (body) => api.post("/admin/pois", body).then((r) => r.data),
  updatePOI: (id, body) => api.patch(`/admin/pois/${id}`, body).then((r) => r.data),
  deletePOI: (id) => api.delete(`/admin/pois/${id}`).then((r) => r.data),
  quotePrice: ({ origin_lat, origin_lng, dest_lat, dest_lng, vehicle_type = "sedan", scheduled_at = null }) => {
    const params = new URLSearchParams({
      origin_lat, origin_lng, dest_lat, dest_lng, vehicle_type,
      ...(scheduled_at ? { scheduled_at } : {}),
    });
    return api.get(`/pricing/quote?${params.toString()}`).then((r) => r.data);
  },
  botLogsClear:  () => api.delete("/bot/logs").then((r) => r.data),
  // Diagnóstico interno — dedup del backend (solo super_admin)
  dedupState:    () => api.get("/admin/dedup-state").then((r) => r.data),
  // Pricing bands (franjas horarias con multiplicador global)
  pricingBands:        () => api.get("/pricing-bands").then((r) => r.data),
  pricingBandCreate:   (body) => api.post("/pricing-bands", body).then((r) => r.data),
  pricingBandUpdate:   (id, body) => api.patch(`/pricing-bands/${id}`, body).then((r) => r.data),
  pricingBandDelete:   (id) => api.delete(`/pricing-bands/${id}`).then((r) => r.data),
  pricingBandActive:   () => api.get("/pricing-bands/active").then((r) => r.data),
};
